import React from 'react';
import { X, Truck, MapPin, Package, MessageCircle, DollarSign, Calendar, Clock, User, Phone, Mail, Bike, Store, Trash2 } from 'lucide-react';

interface OrderDetailsModalProps {
    order: any;
    isOpen: boolean;
    onClose: () => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ order, isOpen, onClose }) => {
    if (!isOpen || !order) return null;

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Data não disponível';
        return new Date(dateString).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status: string) => {
        const statusMap: any = {
            'PAID': { label: 'PAGO', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
            'PENDING': { label: 'PENDENTE', color: 'bg-amber-100 text-amber-700 border-amber-200' },
            'SHIPPED': { label: 'ENVIADO', color: 'bg-blue-100 text-blue-700 border-blue-200' },
            'DELIVERED': { label: 'ENTREGUE', color: 'bg-gray-100 text-gray-700 border-gray-200' },
            'CANCELED': { label: 'CANCELADO', color: 'bg-red-100 text-red-700 border-red-200' }
        };
        const config = statusMap[status] || { label: status || 'DESCONHECIDO', color: 'bg-gray-100 text-gray-700 border-gray-200' };
        return <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${config.color}`}>{config.label}</span>;
    };

    const whatsappLink = `https://wa.me/${(order.customer_phone || '').replace(/\D/g, '')}?text=Olá ${order.customer_name || 'Cliente'}, estou entrando em contato sobre o seu pedido #${(order.id || '').slice(0, 8)} na nossa loja.`;

    const shippingAddress = order.shipping_address;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
                {/* Header */}
                <div className="p-6 border-b border-surface-100 flex justify-between items-center bg-surface-50/50">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-bold text-xl text-surface-900">Pedido #{(order.id || '').slice(0, 8)}</h3>
                            {getStatusBadge(order.status)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Calendar size={14} />
                            <span>{formatDate(order.created_at)}</span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white rounded-xl shadow-sm border border-surface-200 transition-all text-gray-400 hover:text-red-500"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* NOVO: MÉTODO DE PAGAMENTO (ALERTA) */}
                {order.payment_method && (
                    <div className="px-6 py-2 bg-brand-50 border-b border-brand-100 flex items-center justify-between">
                        <span className="text-[10px] font-black text-brand-700 uppercase tracking-widest flex items-center gap-2">
                            <DollarSign size={14} /> Método de Pagamento:
                        </span>
                        <span className="text-xs font-bold text-brand-900">
                            {order.payment_method === 'DELIVERY' ? 'PAGAMENTO NA ENTREGA' : 
                             order.payment_method === 'DELIVERY_PIX' ? 'PAGAMENTO NA ENTREGA (PIX)' :
                             order.payment_method === 'DELIVERY_CREDIT' ? 'PAGAMENTO NA ENTREGA (CARTÃO CRÉDITO)' :
                             order.payment_method === 'DELIVERY_DEBIT' ? 'PAGAMENTO NA ENTREGA (CARTÃO DÉBITO)' :
                             order.payment_method === 'DELIVERY_CASH' ? 'PAGAMENTO NA ENTREGA (DINHEIRO)' :
                             order.payment_method}
                        </span>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">

                    {/* Sessão: Cliente */}
                    <section>
                        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <User size={14} className="text-brand-500" /> Dados do Cliente
                        </h4>
                        <div className="bg-surface-50 rounded-2xl p-4 border border-surface-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-600">
                                    <User size={20} />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-surface-900">{order.customer_name || 'Consumidor'}</p>
                                    <p className="text-xs text-gray-500">{order.customer_email || 'Sem e-mail'}</p>
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                                        <Phone size={20} />
                                    </div>
                                    <p className="text-sm font-bold text-surface-900">{order.customer_phone || 'N/A'}</p>
                                </div>
                                <a
                                    href={whatsappLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors shadow-sm"
                                    title="Chamar no WhatsApp"
                                >
                                    <MessageCircle size={18} />
                                </a>
                            </div>
                        </div>
                    </section>

                    {/* Sessão: Logística */}
                    <section>
                        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <Truck size={14} className="text-brand-500" /> Logística e Entrega
                        </h4>
                        <div className="bg-surface-50 rounded-2xl p-4 border border-surface-100">
                            {order.shipping_type === 'PICKUP' ? (
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 shrink-0">
                                        <Store size={24} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-surface-900">Retirada no Balcão</p>
                                        <p className="text-xs text-gray-500">O cliente virá buscar o pedido no seu estabelecimento.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                                        <MapPin size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start mb-1">
                                            <p className="text-sm font-bold text-surface-900">Endereço de Entrega</p>
                                            <span className="text-[10px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded border border-blue-100">DELIVERY</span>
                                        </div>
                                        {shippingAddress && typeof shippingAddress === 'object' ? (
                                            <div className="text-xs text-gray-600 leading-relaxed">
                                                <p className="font-medium text-surface-800">{shippingAddress.street || 'Rua não informada'}, {shippingAddress.number || 'SN'} {shippingAddress.complement && `- ${shippingAddress.complement}`}</p>
                                                <p>{shippingAddress.neighborhood || ''} - {shippingAddress.city || ''}/{shippingAddress.state || ''}</p>
                                                <p className="mt-1 font-mono text-[10px] bg-white w-fit px-1.5 py-0.5 rounded border border-surface-200">CEP: {shippingAddress.zipcode || 'N/A'}</p>
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-400 italic">Informação de logística indisponível para este pedido antigo.</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Sessão: Itens */}
                    <section>
                        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <Package size={14} className="text-brand-500" /> Itens do Pedido ({order.items?.length || 0})
                        </h4>
                        <div className="border border-surface-100 rounded-2xl overflow-hidden divide-y divide-surface-100">
                            {order.items?.map((item: any, idx: number) => (
                                <div key={idx} className="p-4 flex items-center gap-4 bg-white">
                                    <div className="w-14 h-14 rounded-xl border border-surface-100 overflow-hidden shrink-0 bg-surface-50">
                                        {item.image_url ? (
                                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                                                <Package size={20} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-surface-900 truncate" title={item.name}>{item.name || 'Produto'}</p>
                                        {item.variations && (
                                            <p className="text-[10px] text-gray-500 italic mt-0.5">
                                                {Object.entries(item.variations).map(([k, v]: any) => `${k}: ${v}`).join(' | ')}
                                            </p>
                                        )}
                                        <p className="text-xs font-medium text-brand-600 mt-1">
                                            {item.qty || item.quantity || 0} x R$ {Number(item.price || 0).toFixed(2)}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-sm font-bold text-surface-900">R$ {Number(item.subtotal || 0).toFixed(2)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                {/* Footer: Financeiro */}
                <div className="p-6 bg-surface-50/50 border-t border-surface-100">
                    <div className="space-y-2 mb-6">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Subtotal</span>
                            <span className="font-medium text-surface-900">R$ {(Number(order.total_amount || 0) - (Number(order.delivery_info?.price || 0)) + (Number(order.discount_amount || 0))).toFixed(2)}</span>
                        </div>
                        {Number(order.delivery_info?.price || 0) > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Frete ({order.delivery_method || 'Entrega'})</span>
                                <span className="font-medium text-emerald-600">+ R$ {Number(order.delivery_info?.price || 0).toFixed(2)}</span>
                            </div>
                        )}
                        {Number(order.discount_amount || 0) > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Desconto</span>
                                <span className="font-medium text-red-600">- R$ {Number(order.discount_amount || 0).toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between pt-2 border-t border-surface-200">
                            <span className="font-bold text-surface-900 text-lg uppercase tracking-tight">Total</span>
                            <span className="font-black text-brand-600 text-2xl tracking-tighter italic">R$ {Number(order.total_amount || 0).toFixed(2)}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-3 bg-white border border-surface-200 text-surface-700 rounded-xl font-bold hover:bg-surface-50 transition-colors"
                        >
                            Fechar
                        </button>
                        <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-3 bg-emerald-500 text-white rounded-xl font-bold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-emerald-200"
                        >
                            <MessageCircle size={20} />
                            Falar com Cliente
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsModal;
